import AddIcon from "@mui/icons-material/AddRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox.tsx";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../../../../../api/biApi";
import type { MeasureDescriptor, MeasureSubgroup } from "../../../../../../../../../api/types/svTypes.ts";
import {
  selectCurrentDataSetId,
  selectMeasureGroups,
  selectAvailableMeasureFields,
  selectMeasuresForCurrentReportType,
  selectMeasuresStructure,
  selectMeasureSubgroups,
} from "../../../../../store/selectors/metaDataSelectors";
import { createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { DataSetIds } from "../../../../../utilities/DimensionNames";
import ChartOfAccounts from "../../chartOfAccounts/ChartOfAccounts.tsx";
import CustomMeasureDialog from "../../customMeasure/CustomMeasureDialog.tsx";
import DeleteMeasureDialog from "../../customMeasure/delete/DeleteMeasureDialog.tsx";
import ExplainMeasureDialog from "../../customMeasure/ExplainMeasureDialog.tsx";
import DeleteSubgroupDialog from "../../customMeasure/subgroup/DeleteSubgroupDialog.tsx";
import MoveToSubgroupDialog from "../../customMeasure/subgroup/MoveToSubgroupDialog.tsx";
import SubgroupDialog from "../../customMeasure/subgroup/SubgroupDialog.tsx";
import UsedByReportsDialog from "../../customMeasure/used-by/UsedByReportsDialog.tsx";
import { getGroupSubgroups } from "../../customMeasure/utilities/measureSubgroups.ts";
import type { FieldsSectionControl } from "../fieldsExpansionState.ts";
import FieldsSectionHeader from "../FieldsSectionHeader.tsx";
import { buildMeasureSubgroupTree, getVisibleMeasureTreeGroups } from "./buildMeasureSubgroupTree";
import type { MeasureGroupOptionsTarget } from "./MeasureGroupMenu.tsx";
import MeasureGroupMenu from "./MeasureGroupMenu.tsx";
import MeasureMenu from "./MeasureMenu.tsx";
import MeasuresTree from "./MeasuresTree.tsx";

const lockCustomMeasure = withErrorHandling(biApi.lockCustomMeasure);
const unlockCustomMeasure = withErrorHandling(biApi.unlockCustomMeasure);

interface Props {
  searchText: string;
  disabled: boolean;
  section?: FieldsSectionControl;
}

const MeasuresWrapper = ({ searchText, disabled, section }: Props) => {
  const allFields = useSelector(selectAvailableMeasureFields);

  if (allFields.length === 0) {
    return null;
  }

  return <Measures searchText={searchText} disabled={disabled} section={section} />;
};

const Measures = ({ searchText, disabled, section }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const allFields = useSelector(selectAvailableMeasureFields);
  const { hasAnyPermission } = useClientPermissionsContext();
  const measures = useSelector(selectMeasuresForCurrentReportType);
  const measuresStructure = useSelector(selectMeasuresStructure);
  const measureGroups = useSelector(selectMeasureGroups);
  const measureSubgroups = useSelector(selectMeasureSubgroups);
  const dataSetId = useSelector(selectCurrentDataSetId);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [measuresExpanded, setMeasuresExpanded] = useState(true);
  const [showAddCustomMeasure, setShowAddCustomMeasure] = useState(false);
  const [measureToEdit, setMeasureToEdit] = useState<{ measure: MeasureDescriptor; group?: string }>();
  const [measureToCopy, setMeasureToCopy] = useState<MeasureDescriptor>();
  const [measureToDelete, setMeasureToDelete] = useState<MeasureDescriptor>();
  const [measureToExplain, setMeasureToExplain] = useState<MeasureDescriptor>();
  const [measureToShowUsedBy, setMeasureToShowUsedBy] = useState<MeasureDescriptor>();
  const [measureForChartOfAccounts, setMeasureForChartOfAccounts] = useState<MeasureDescriptor>();
  const [measureOptions, setMeasureOptions] = useState<{
    x: number;
    y: number;
    measure: MeasureDescriptor;
    group: string;
  }>();
  const [measureToMove, setMeasureToMove] = useState<MeasureDescriptor>();
  const [groupOptions, setGroupOptions] = useState<{ x: number; y: number } & MeasureGroupOptionsTarget>();
  const [groupForNewSubgroup, setGroupForNewSubgroup] = useState<string>();
  const [subgroupToEdit, setSubgroupToEdit] = useState<MeasureSubgroup>();
  const [subgroupToDelete, setSubgroupToDelete] = useState<MeasureSubgroup>();
  const defaultGroup = measureToEdit?.group || measureGroups[0];

  const measureTreeGroups = useMemo(
    () => buildMeasureSubgroupTree(measuresStructure, measureSubgroups, measures),
    [measuresStructure, measureSubgroups, measures]
  );

  const filteredFields = useMemo(() => {
    if (searchText === "") {
      return allFields;
    }
    return allFields.filter((d) => d.caption.toLowerCase().indexOf(searchText) > -1);
  }, [allFields, searchText]);

  const explainMeasure = useCallback(
    (measure: MeasureDescriptor) => {
      setMeasureOptions(undefined);
      setMeasureToExplain(measure);
    },
    [setMeasureOptions]
  );
  const editMeasure = useCallback(
    (measure: MeasureDescriptor) => {
      setMeasureOptions(undefined);
      setMeasureToEdit({ measure, group: measureOptions?.group });
    },
    [measureOptions]
  );
  const createMeasureDuplicate = useCallback((measure: MeasureDescriptor) => {
    setMeasureOptions(undefined);
    setMeasureToCopy(measure);
  }, []);
  const deleteMeasure = useCallback((measure: MeasureDescriptor) => {
    setMeasureOptions(undefined);
    setMeasureToDelete(measure);
  }, []);
  const moveMeasureToSubgroup = useCallback((measure: MeasureDescriptor) => {
    setMeasureOptions(undefined);
    setMeasureToMove(measure);
  }, []);
  const createSubgroup = useCallback((group: string) => {
    setGroupOptions(undefined);
    setGroupForNewSubgroup(group);
  }, []);
  const editSubgroup = useCallback((subgroup: MeasureSubgroup) => {
    setGroupOptions(undefined);
    setSubgroupToEdit(subgroup);
  }, []);
  const deleteSubgroup = useCallback((subgroup: MeasureSubgroup) => {
    setGroupOptions(undefined);
    setSubgroupToDelete(subgroup);
  }, []);

  const openChartOfAccounts = useCallback((measure: MeasureDescriptor) => {
    setMeasureOptions(undefined);
    setMeasureForChartOfAccounts(measure);
  }, []);

  const onShowUsedByReports = useCallback((measure: MeasureDescriptor) => {
    setMeasureOptions(undefined);
    setMeasureToShowUsedBy(measure);
  }, []);

  const toggleMeasureLock = useCallback(
    async (measure: MeasureDescriptor) => {
      setIsLoading(true);
      try {
        const id = measure.measureId ?? measure.name;
        const wasLocked = measure.isLocked === true;
        const [data, error] = wasLocked ? await unlockCustomMeasure(id) : await lockCustomMeasure(id);
        if (error || !data) {
          if (error) {
            logError(error, "[Measures] toggleMeasureLock");
          }
          sendNotificationError(
            wasLocked
              ? "An error occurred while unlocking the measure for editing."
              : "An error occurred while locking the measure for editing."
          );
        } else {
          dispatch(createMetaDataAction("updateMeasure", data));
          sendNotification(
            `The measure "${measure.caption}" has been ${wasLocked ? "unlocked" : "locked"} for editing.`
          );
        }
      } finally {
        setIsLoading(false);
        setMeasureOptions(undefined);
      }
    },
    [dispatch, sendNotification, sendNotificationError, setMeasureOptions]
  );

  const onShowCustomMeasureDialog = useCallback(() => setShowAddCustomMeasure(true), []);
  const offShowCustomMeasureDialog = useCallback(() => setShowAddCustomMeasure(false), []);
  const revertExpansion = useCallback(() => setMeasuresExpanded((ov) => !ov), []);
  const onShowOptions = useCallback((x: number, y: number, measure: MeasureDescriptor, group: string) => {
    setMeasureOptions({ x, y, measure, group });
  }, []);
  const onShowGroupOptions = useCallback((x: number, y: number, target: MeasureGroupOptionsTarget) => {
    setGroupOptions({ x, y, ...target });
  }, []);

  const clearMeasureToEdit = useCallback(() => setMeasureToEdit(undefined), []);
  const clearMeasureToCopy = useCallback(() => setMeasureToCopy(undefined), []);
  const clearMeasureToDelete = useCallback(() => setMeasureToDelete(undefined), []);
  const clearMeasureToExplain = useCallback(() => setMeasureToExplain(undefined), []);
  const clearMeasureChartOfAccounts = useCallback(() => setMeasureForChartOfAccounts(undefined), []);
  const clearMeasureOptions = useCallback(() => setMeasureOptions(undefined), []);
  const clearMeasureToShowUsedBy = useCallback(() => setMeasureToShowUsedBy(undefined), []);
  const clearMeasureToMove = useCallback(() => setMeasureToMove(undefined), []);
  const clearGroupOptions = useCallback(() => setGroupOptions(undefined), []);
  const clearGroupForNewSubgroup = useCallback(() => setGroupForNewSubgroup(undefined), []);
  const clearSubgroupToEdit = useCallback(() => setSubgroupToEdit(undefined), []);
  const clearSubgroupToDelete = useCallback(() => setSubgroupToDelete(undefined), []);

  const expanded = section?.open ?? measuresExpanded;
  const showEmptySubgroups = hasAnyPermission(["ManageCustomMeasures"]) && !disabled && searchText === "";
  const supportsMeasureSubgroups = dataSetId === DataSetIds.GeneralAndMemo;
  const canManageMeasureSubgroups = supportsMeasureSubgroups && hasAnyPermission(["ManageCustomMeasures"]) && !disabled;
  const visibleMeasureTreeGroups = getVisibleMeasureTreeGroups(
    measureTreeGroups,
    new Set(filteredFields.map((field) => field.name)),
    showEmptySubgroups
  );

  const addCustomMeasureButton = (
    <AuthorizedBox permissions={["ManageCustomMeasures"]}>
      <IconButton color="primary" onClick={onShowCustomMeasureDialog} disabled={disabled}>
        <AddIcon />
      </IconButton>
    </AuthorizedBox>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {section ? (
        <FieldsSectionHeader
          title="Measures"
          open={section.open}
          onToggle={section.onToggle}
          actions={addCustomMeasureButton}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ flexGrow: 1, color: "text.primary" }} variant="subtitle2">
            Measures
          </Typography>
          {addCustomMeasureButton}
          <IconButton onClick={revertExpansion}>
            {measuresExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}

      <Collapse
        orientation="vertical"
        in={expanded}
        sx={{ "& .MuiCollapse-wrapperInner": { display: "block" }, ...(section && { pl: 2 }) }}
      >
        <MeasuresTree
          key={section?.groupsEpoch}
          fields={filteredFields}
          groups={visibleMeasureTreeGroups}
          activeMeasureName={measureOptions?.measure.name}
          onShowOptions={onShowOptions}
          disabled={disabled}
          expanded={searchText !== "" || section?.groupsOpen === true}
          visible={expanded}
          customMeasureGroups={measureGroups}
          onShowGroupOptions={canManageMeasureSubgroups ? onShowGroupOptions : undefined}
        />
      </Collapse>
      {showAddCustomMeasure && <CustomMeasureDialog onClose={offShowCustomMeasureDialog} defaultGroup={defaultGroup} />}
      {measureToEdit && (
        <CustomMeasureDialog measure={measureToEdit.measure} defaultGroup={defaultGroup} onClose={clearMeasureToEdit} />
      )}
      {measureToCopy && (
        <CustomMeasureDialog sourceMeasure={measureToCopy} defaultGroup={defaultGroup} onClose={clearMeasureToCopy} />
      )}
      {measureToDelete && <DeleteMeasureDialog measure={measureToDelete} onCancel={clearMeasureToDelete} />}
      {measureToExplain && (
        <ExplainMeasureDialog
          measure={measureToExplain}
          onClose={clearMeasureToExplain}
          onEdit={editMeasure}
          readonly={disabled}
        />
      )}
      {measureForChartOfAccounts !== undefined && (
        <ChartOfAccounts initialMeasure={measureForChartOfAccounts} onClose={clearMeasureChartOfAccounts} />
      )}
      {measureToShowUsedBy && <UsedByReportsDialog measure={measureToShowUsedBy} onClose={clearMeasureToShowUsedBy} />}
      {measureToMove && <MoveToSubgroupDialog measure={measureToMove} onClose={clearMeasureToMove} />}
      {groupForNewSubgroup !== undefined && (
        <SubgroupDialog
          group={groupForNewSubgroup}
          existingSubgroups={getGroupSubgroups(measureSubgroups, groupForNewSubgroup)}
          onClose={clearGroupForNewSubgroup}
        />
      )}
      {subgroupToEdit && (
        <SubgroupDialog
          subgroup={subgroupToEdit}
          existingSubgroups={getGroupSubgroups(measureSubgroups, subgroupToEdit.group)}
          onClose={clearSubgroupToEdit}
        />
      )}
      {subgroupToDelete && <DeleteSubgroupDialog subgroup={subgroupToDelete} onClose={clearSubgroupToDelete} />}
      {groupOptions !== undefined && (
        <MeasureGroupMenu
          {...groupOptions}
          onCreateSubgroup={createSubgroup}
          onEditSubgroup={editSubgroup}
          onDeleteSubgroup={deleteSubgroup}
          onClose={clearGroupOptions}
        />
      )}
      {measureOptions !== undefined && (
        <MeasureMenu
          {...measureOptions}
          readonly={disabled}
          onExplain={explainMeasure}
          onEdit={editMeasure}
          onMoveToSubgroup={supportsMeasureSubgroups ? moveMeasureToSubgroup : undefined}
          onDuplicate={createMeasureDuplicate}
          onDelete={deleteMeasure}
          onToggleLock={toggleMeasureLock}
          onClose={clearMeasureOptions}
          onChartOfAccounts={openChartOfAccounts}
          onUsedByReports={onShowUsedByReports}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
};

export default MeasuresWrapper;
